/**
 * CATÁLOGO DE PERMISSÕES — para administrar 216 códigos sem adivinhação.
 *
 * A tela de permissões mostrava apenas a lista crua de `customPermissions`
 * separada por "•". Com 216 códigos, escolher entre `me_report.approve` e
 * `technical_review.approve` virava tentativa e erro — e errar aqui concede
 * ou nega acesso real.
 *
 * Este módulo agrupa por DOMÍNIO, dá um nome em português a cada código e
 * oferece busca. Ele não decide nada: a autorização continua sendo
 * `resolveEffectivePermissions` + `RbacAuthorizationService`.
 *
 * ────────────────────────────────────────────────────────────────────
 * LIMITE DECLARADO: NÃO EXISTE REVOGAÇÃO INDIVIDUAL.
 * ────────────────────────────────────────────────────────────────────
 *
 * `customPermissions` é uma lista plana de CONCESSÕES somadas ao perfil, não
 * uma estrutura {concedidas, negadas}. Retirar de uma pessoa algo que o
 * perfil dela concede exigiria mudar o modelo e todos os pontos de
 * verificação — ver docs/RBAC.md. Enquanto isso não for feito, a tela
 * precisa DIZER isso, e não desenhar uma caixa de "revogar" que não teria
 * efeito: um controle que mente é pior que um controle ausente.
 */

import { PermissionCode } from "./access-types.js";

/** Os agrupamentos oferecidos na administração. */
export type PermissionGroupKey =
  | "CADASTROS" | "INSPECOES" | "END" | "RELATORIOS" | "INTEGRIDADE"
  | "ENGENHARIA" | "GESTAO" | "ADMINISTRACAO" | "OUTROS";

export const PERMISSION_GROUP_LABELS: Record<PermissionGroupKey, string> = {
  CADASTROS: "Cadastros",
  INSPECOES: "Inspeções",
  END: "Ensaios Não Destrutivos",
  RELATORIOS: "Relatórios",
  INTEGRIDADE: "Integridade",
  ENGENHARIA: "Engenharia e PLH",
  GESTAO: "Gestão",
  ADMINISTRACAO: "Administração",
  OUTROS: "Outros"
};

/** Prefixo do código → grupo. O prefixo é o domínio real do PermissionCode. */
const GROUP_OF_PREFIX: Readonly<Record<string, PermissionGroupKey>> = {
  client: "CADASTROS", project: "CADASTROS", unit_area: "CADASTROS",
  equipment: "CADASTROS", instrument: "CADASTROS", professional: "CADASTROS",
  signatory: "CADASTROS", catalog: "CADASTROS", document: "CADASTROS",

  inspection: "INSPECOES", inspector: "INSPECOES", checklist: "INSPECOES",
  field_control: "INSPECOES",

  lp_report: "END", pm_report: "END", me_report: "END", usc_report: "END", bscan_report: "END",
  ndt: "END", ndt_registry: "END",

  report: "RELATORIOS", ri: "RELATORIOS", minuta: "RELATORIOS",
  recommendation: "RELATORIOS",

  integrity_report: "INTEGRIDADE", avr: "INTEGRIDADE", calculation: "INTEGRIDADE",

  engineering: "ENGENHARIA", technical_review: "ENGENHARIA", plh: "ENGENHARIA",
  signature: "ENGENHARIA",

  management: "GESTAO", rdo: "GESTAO", sub_os: "GESTAO", sub_work_order: "GESTAO", dashboard: "GESTAO",
  // Códigos em camelCase, herdados de blocos anteriores. Mapeados aqui em vez
  // de renomeados: o código é o identificador gravado na autorização, e
  // renomeá-lo revogaria silenciosamente o que já foi concedido.
  professionalSubOs: "GESTAO",
  safetyDevice: "CADASTROS",
  technicalAsset: "CADASTROS",

  administration: "ADMINISTRACAO", identity: "ADMINISTRACAO", user: "ADMINISTRACAO",
  rbac: "ADMINISTRACAO", audit: "ADMINISTRACAO", settings: "ADMINISTRACAO",
  backup: "ADMINISTRACAO", system: "ADMINISTRACAO",
  studio: "ADMINISTRACAO", cortex: "ADMINISTRACAO",
  ai_review: "RELATORIOS", ai_assistant: "RELATORIOS", ai_settings: "ADMINISTRACAO"
};

/** Verbo do código → verbo em português. */
const ACTION_LABELS: Readonly<Record<string, string>> = {
  access: "Acessar", read: "Visualizar", view: "Visualizar", view_queue: "Ver a fila",
  create: "Criar", update: "Editar", edit: "Editar", delete: "Excluir", restore: "Restaurar",
  manage: "Administrar", approve: "Aprovar", sign: "Assinar", validate: "Validate",
  submit_to_plh: "Enviar ao PLH", request_correction: "Solicitar correção",
  review: "Analisar", generate_pdf: "Gerar PDF", search: "Pesquisar",
  assign_inspector: "Designar Inspetor", start_analysis: "Iniciar análise",
  assign: "Vincular", remove: "Desvincular", inactivate: "Inativar", reactivate: "Reativar",
  "database.reset": "Resetar base operacional do",
  "documents.manage": "Administrar documentos de",
  "workflow.update": "Editar workflow em", "import.update": "Editar importações em",
  "signature.read": "Visualizar assinatura ICP em", "signature.update": "Editar assinatura ICP em",
  "integration.read": "Visualizar integrações em", "integration.update": "Editar integrações em",
  "security.read": "Visualizar segurança em", "security.update": "Editar segurança em",
  "diagnostics.read": "Visualizar diagnóstico em", "diagnostics.execute": "Executar diagnóstico em",
  "audit.read": "Visualizar histórico de",
  "identity.read": "Visualizar identidade em", "identity.update": "Editar identidade em", "theme.read": "Visualizar tema em", "theme.update": "Editar tema em",
  "login.update": "Editar tela de login em", "documents.read": "Visualizar relatórios em", "documents.update": "Editar relatórios em",
  "templates.read": "Visualizar templates em", "templates.update": "Editar templates em", "test": "Testar", "publish": "Publicar", "archive": "Arquivar",
  "share.create": "Criar link compartilhado do", "share.read": "Visualizar links compartilhados do",
  "share.revoke": "Revogar links compartilhados do", "share.manage": "Administrar todos os links compartilhados do",
  execute: "Executar", apply_language: "Aplicar correções de português do", resolve_finding: "Resolver alertas do", history: "Ver histórico do", use: "Usar"
};

/** Domínio do código → substantivo em português. */
const SUBJECT_LABELS: Readonly<Record<string, string>> = {
  client: "Clientes", project: "Projetos", unit_area: "Unidades / Áreas",
  equipment: "Equipamentos", instrument: "Instrumentos", professional: "Profissionais",
  signatory: "Signatários", catalog: "Catálogos", document: "Documentos",
  inspection: "Inspeções", inspector: "Inspetores", checklist: "Checklists",
  field_control: "Controle de Campo",
  lp_report: "Relatório LP", pm_report: "Relatório PM", me_report: "Relatório ME",
  usc_report: "Relatório US-C", bscan_report: "Relatório B-Scan / C-Scan", ndt: "END", ndt_registry: "Registro de END",
  report: "Relatórios", ri: "Relatório de Integridade", minuta: "Minutas",
  recommendation: "Recomendações",
  integrity_report: "Relatório de Integridade", avr: "AVR", calculation: "Cálculos",
  engineering: "Engenharia", technical_review: "Revisão Técnica", plh: "PLH",
  signature: "Assinatura",
  management: "Gestão", rdo: "RDO", sub_os: "Sub-OS", sub_work_order: "Sub-OS", dashboard: "Painel de Indicadores",
  professionalSubOs: "Profissional na Sub-OS", safetyDevice: "Dispositivos de Segurança",
  technicalAsset: "Ativos Técnicos",
  administration: "Administração", identity: "Identidade", user: "Usuários",
  rbac: "Perfis e Permissões", audit: "Auditoria", settings: "Configurações",
  backup: "Backup e Restauração", system: "Sistema",
  studio: "Studio", cortex: "Córtex (descontinuado)",
  ai_review: "INSPECT IA REVIEW", ai_assistant: "Assistente Técnico IA", ai_settings: "Configurações de IA"
};

export interface PermissionDescriptor {
  /** O código real, ex.: "equipment.read". */
  readonly code: string;
  readonly group: PermissionGroupKey;
  /** "Visualizar Equipamentos" — o código continua visível ao lado na tela. */
  readonly label: string;
}

function describe(code: string): PermissionDescriptor {
  const dot = code.indexOf(".");
  const prefix = dot > 0 ? code.slice(0, dot) : code;
  const action = dot > 0 ? code.slice(dot + 1) : "";
  const subject = SUBJECT_LABELS[prefix] ?? prefix;
  const verb = ACTION_LABELS[action] ?? action.replace(/_/g, " ");
  return {
    code,
    group: GROUP_OF_PREFIX[prefix] ?? "OUTROS",
    // Sem verbo conhecido o código aparece como está: inventar um rótulo
    // bonito para uma permissão que ninguém mapeou esconde o desconhecimento.
    label: verb ? `${verb} ${subject}` : subject
  };
}

/** Todo o catálogo, ordenado por grupo e depois por rótulo. */
export const PERMISSION_CATALOG: readonly PermissionDescriptor[] = Object.freeze(
  Object.values(PermissionCode)
    .map(code => describe(String(code)))
    .sort((a, b) => a.group.localeCompare(b.group) || a.label.localeCompare(b.label, "pt-BR"))
);

export function permissionDescriptor(code: string): PermissionDescriptor {
  return PERMISSION_CATALOG.find(p => p.code === code) ?? describe(code);
}

/** Agrupado, na ordem de `PERMISSION_GROUP_LABELS`, sem grupos vazios. */
export function permissionsByGroup(
  catalog: readonly PermissionDescriptor[] = PERMISSION_CATALOG
): Array<{ group: PermissionGroupKey; label: string; permissions: PermissionDescriptor[] }> {
  const order = Object.keys(PERMISSION_GROUP_LABELS) as PermissionGroupKey[];
  return order
    .map(group => ({
      group,
      label: PERMISSION_GROUP_LABELS[group],
      permissions: catalog.filter(p => p.group === group)
    }))
    .filter(g => g.permissions.length > 0);
}

/** Busca por rótulo OU por código, sem acento e sem caixa. */
export function searchPermissions(query: string, group?: PermissionGroupKey | "ALL"): PermissionDescriptor[] {
  const term = (query || "").normalize("NFD").replace(/[̀-ͯ]/g, "").toLowerCase().trim();
  return PERMISSION_CATALOG.filter(p => {
    if (group && group !== "ALL" && p.group !== group) return false;
    if (!term) return true;
    const label = p.label.normalize("NFD").replace(/[̀-ͯ]/g, "").toLowerCase();
    return label.includes(term) || p.code.toLowerCase().includes(term);
  });
}

/* ══════════════════════════════════════════════════════════════════════
   PERMISSÕES EFETIVAS — o que a pessoa realmente tem, e de onde vem
   ══════════════════════════════════════════════════════════════════════ */

export type PermissionOrigin = "ROLE" | "INDIVIDUAL";

export interface EffectivePermissionRow {
  readonly descriptor: PermissionDescriptor;
  readonly granted: boolean;
  /** De onde veio. `null` quando a pessoa não tem a permissão. */
  readonly origin: PermissionOrigin | null;
}

export interface EffectivePermissionSummary {
  readonly fromRole: number;
  readonly individual: number;
  readonly effective: number;
  readonly rows: readonly EffectivePermissionRow[];
}

/**
 * Cruza o que o PERFIL concede com o que foi concedido INDIVIDUALMENTE.
 *
 * Uma permissão que o perfil já dá continua marcada como `ROLE` mesmo se
 * também constar nas individuais: duplicá-la não acrescenta poder, e
 * apresentá-la como individual sugeriria que retirá-la faria diferença —
 * não faria, porque o perfil a concede de qualquer modo.
 */
export function summarizeEffectivePermissions(
  rolePermissions: ReadonlySet<string> | readonly string[],
  individualPermissions: readonly string[] = []
): EffectivePermissionSummary {
  const role = rolePermissions instanceof Set ? rolePermissions : new Set(rolePermissions);
  const individual = new Set(individualPermissions.map(String));

  const rows: EffectivePermissionRow[] = PERMISSION_CATALOG.map(descriptor => {
    const fromRole = role.has(descriptor.code);
    const fromIndividual = individual.has(descriptor.code);
    return {
      descriptor,
      granted: fromRole || fromIndividual,
      origin: fromRole ? "ROLE" : fromIndividual ? "INDIVIDUAL" : null
    };
  });

  return {
    fromRole: rows.filter(r => r.origin === "ROLE").length,
    individual: rows.filter(r => r.origin === "INDIVIDUAL").length,
    effective: rows.filter(r => r.granted).length,
    rows
  };
}

/**
 * O aviso que a tela deve exibir sobre o limite do modelo.
 *
 * Fica aqui, e não escrito na página, para que qualquer tela que administre
 * permissões diga a mesma coisa — e para que remover o limite signifique
 * apagar uma constante, não caçar textos.
 */
export const INDIVIDUAL_REVOCATION_UNSUPPORTED =
  "Permissões individuais são ACRÉSCIMOS ao perfil. Não é possível retirar de uma pessoa algo que o perfil dela concede — para isso, altere o perfil. (Limitação registrada em docs/RBAC.md.)";
